// Pure functions for the user-managed Want List — no side effects, fully testable.
// See CONTEXT.md "Want List" / "Want List Entry" and ADR-0009.

/**
 * Parse a URL path into a want-list route descriptor.
 * Matches /u/@<handle>/wantlist (including /u/@me/wantlist). The bare
 * /u/@<handle> is the user page (`user-page-logic.parseUserPageRoute`).
 * Returns null for non-want-list paths.
 *
 * The handle is the (globally-unique) Discord username slug; '@me' resolves to
 * the logged-in caller. The canonical owner key (discord_id) is resolved
 * server-side — a stale username 404s there, never here.
 *
 * @param {string} path - window.location.pathname
 * @returns {{ view: 'wantlist', params: { handle: string } } | null}
 */
export function parseWantListRoute(path) {
  if (!path || !path.startsWith('/u/')) return null;
  const parts = path.split('/').filter(Boolean); // ['u', '@handle', 'wantlist'?]
  if (parts.length < 2 || parts[0] !== 'u') return null;

  const rawHandle = parts[1];
  if (!rawHandle.startsWith('@')) return null;
  // Strip the leading '@' for the param; keep it normalized lower-case for @me.
  const handle = rawHandle.slice(1);
  if (!handle) return null;

  if (parts.length === 3 && parts[2] === 'wantlist') {
    return { view: 'wantlist', params: { handle } };
  }
  return null;
}

/**
 * Build the want-page URL for a handle, optionally filtered to one Project
 * (`project` = the short URL without its slash, e.g. 'mfd.xyz').
 * @param {string} [handle] - bare username (no '@') or 'me' (the default)
 * @param {{ project?: string|null }} [opts]
 * @returns {string}
 */
export function buildWantListUrl(handle, { project } = {}) {
  const h = handle || 'me';
  const slug = h.startsWith('@') ? h : `@${h}`;
  const base = `/u/${slug}/wantlist`;
  return project ? `${base}?project=${encodeURIComponent(project)}` : base;
}

/**
 * The route handle for a signed-in user's OWN want list. Prefers their real
 * Discord username slug (so the link/URL is the canonical /u/@<username>/wantlist
 * from the first click, never the generic @me) and falls back to 'me' only when
 * the username isn't cached yet, in which case WantPage rewrites the URL to
 * the real handle after the first successful load.
 * @param {unknown} discordUsername - anything non-string falls back to 'me'
 * @returns {string}
 */
export function selfWantListHandle(discordUsername) {
  const u = typeof discordUsername === 'string' ? discordUsername.trim() : '';
  return u || 'me';
}

/**
 * Normalized Card Name comparison form (mirrors the shared backend
 * `discord_sql.normalized_name_sql`): lower-case, then every non-[a-z0-9 ] run
 * collapsed to a space. Used to compare a card's name against want entries on
 * the client without a round-trip. Diacritic transliteration is intentionally
 * NOT performed — same as the backend form.
 * @param {string} name
 * @returns {string}
 */
export function normalizeName(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ');
}

/**
 * Stable optimistic-override identity for a concrete binder card, shared by the
 * want store (review §9c). Uses the SAME normalized-name form as
 * cardMatchesWants so a tapped star and the server's match logic agree on which
 * card the override applies to — punctuation/case can't make them diverge.
 * @param {{ name?: string, card_name?: string, set_code?: string,
 *           collector_number?: string, finish?: string }} card
 * @returns {string}
 */
export function cardWantKey(card) {
  if (!card) return '';
  const name = normalizeName(card.name ?? card.card_name);
  const set = (card.set_code || '').toLowerCase();
  const cn = card.collector_number || '';
  const finish = (card.finish || 'nonfoil').toLowerCase();
  return `${name}|${set}|${cn}|${finish}`;
}

/**
 * The printing a want hit (`wants_board?hits=`) or a binder card names,
 * as one key: set, collector number and finish. A printing identifies its
 * card, so this is all the in-binder star needs to look a card up among the
 * hits. Reads a hit's `set_code` and the binder card's `set`; both sides carry
 * the stored `finish`, folded as the server matcher folds it
 * (want_list_sql.finish_fold_sql): lower case, missing == 'nonfoil'. '' for a
 * card without a set and collector number: never a hit.
 * @param {{ name?: string, set_code?: string, set?: string, collector_number?: string,
 *           finish?: string | null } | null} card
 * @returns {string}
 */
export function wantHitKey(card) {
  const set = (card?.set_code ?? card?.set ?? '').toLowerCase();
  const cn = card?.collector_number ?? '';
  if (!set || !cn) return '';
  const finish = (card.finish ?? 'nonfoil').toLowerCase();
  return `${set}|${cn}|${finish}`;
}

/**
 * Does a concrete binder card match ANY of the viewer's Want List Entries?
 *
 * Backs the in-binder star's filled state. Specificity-aware (CONTEXT.md "Card
 * Specificity"): an entry matches when normalized names are equal AND each
 * narrowing dimension on the entry is either absent (null/undefined = wildcard)
 * or equal to the card's value. Finish uses the 3-value enum
 * (nonfoil/foil/etched); a missing card finish is treated as 'nonfoil' (the
 * renderer's default), never the display-only `is_foil_finish` boolean.
 *
 * @param {{ name?: string, card_name?: string, set_code?: string, set?: string,
 *           collector_number?: string, finish?: string }} card
 * @param {import('../types.js').WantListEntry[]} entries
 * @returns {boolean}
 */
export function cardMatchesWants(card, entries) {
  if (!card || !entries || entries.length === 0) return false;

  const cName = normalizeName(card.name ?? card.card_name);
  if (!cName) return false;
  const cSet = (card.set_code ?? card.set ?? '').toLowerCase();
  const cCn = card.collector_number ?? '';
  const cFinish = (card.finish ?? 'nonfoil').toLowerCase();

  return entries.some((e) => {
    if (normalizeName(e.card_name) !== cName) return false;
    if (e.set_code != null && e.set_code.toLowerCase() !== cSet) return false;
    if (e.collector_number != null && e.collector_number !== cCn) return false;
    if (e.finish != null && e.finish.toLowerCase() !== cFinish) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
// Projects (ADR-0016): the want page's shareable ?project= param
// ---------------------------------------------------------------------------

/** The `project` query value, trimmed, or null. */
export function parseProjectParam(search) {
  const v = new URLSearchParams(search || '').get('project');
  const t = (v || '').trim();
  return t || null;
}

/** 'mfd.xyz' for a project whose short_url is '/mfd.xyz' (the URL param form). */
export function projectParamOf(project) {
  return (project?.short_url || '').replace(/^\//, '');
}

/** The project whose short URL matches `param`, or null (unknown/absent: the caller's fallback). */
export function selectProject(projects, param) {
  if (!param) return null;
  return (projects || []).find((p) => projectParamOf(p) === param) || null;
}

/**
 * Which bundle the want page should land on after a load: the ONE place the
 * rule lives, so the page doesn't answer "which bundle are we on" three ways.
 *
 * In precedence order:
 *  1. an explicit `select` (an add lands on the new Project, a remove on the
 *     fallback);
 *  2. on the FIRST load of a handle, the shareable `?project=` param; an
 *     unknown/absent one falls back;
 *  3. otherwise keep `current`, unless the Project it names has vanished
 *     (removed, or its source went away), which falls back.
 * `fallback` is the page's non-Project bundle (its Default Want List).
 *
 * @param {{ select?: string, firstLoad?: boolean,
 *           projects?: import('../types.js').Project[],
 *           search?: string, current?: string, fallback: string }} args
 * @returns {string} the fallback | a project_binder_id
 */
export function resolveView({ select, firstLoad, projects, search, current, fallback }) {
  if (select) return select;
  const list = projects || [];
  if (firstLoad) {
    const wanted = selectProject(list, parseProjectParam(search));
    return wanted ? wanted.binder_id : fallback;
  }
  const view = current || fallback;
  if (view === fallback) return view;
  return list.some((p) => p.binder_id === view) ? view : fallback;
}
